import { ChevronDown, Loader2, Plus, X } from 'lucide-react';
import * as React from 'react';
import { CheckSlot } from './check-row';
import { popoverScrollFix } from './lib/popover-scroll-fix';
import { cn } from './lib/utils';
import { Input } from './primitives/input';
import { Popover, PopoverAnchor, PopoverContent } from './primitives/popover';

// Generic, shared autocomplete used across the app. Adopt this for any
// "pick one item from a list with search" UI. Two filtering modes:
//
//   - Client mode: pass `items` (the full list) and `filterFn`. The component
//     filters in memory against the user's typed query.
//   - Server mode: pass `items` (current server result) plus `onQueryChange`
//     and `loading`. The parent debounces and refetches; the component renders
//     whatever it's handed.
//
// Either mode benefits from `onOpenChange`, which mirrors the popover open
// state so parents can gate queries with `enabled: open`.
//
// Items are opaque to the component except via `getId` and `getLabel`; richer
// row layouts plug in through `renderItem`. `renderTrailing` lets the input
// show a chip next to the selected value (e.g. an external id). `renderFooter`
// or the convenience `onCreateNew` slot a CTA into the dropdown.
//
// A11y: implements the WAI-ARIA combobox-with-listbox pattern. Focus stays
// on the input; option highlight is conveyed to assistive tech via
// `aria-activedescendant` referencing the option's id.
export interface ComboboxItemContext {
  isSelected: boolean;
  isHighlighted: boolean;
  query: string;
}

export interface ComboboxGrouping<T> {
  /** Stable per-item group identifier. Items in the same contiguous run
   *  with the same key are treated as one group; reordering is the
   *  caller's responsibility. */
  getKey: (item: T) => string;
  /** Non-interactive header rendered above the first visible item of
   *  each group. */
  renderHeader: (groupKey: string) => React.ReactNode;
}

export interface ComboboxProps<T> {
  items: readonly T[];
  selectedItem?: T | null;
  value: string | null;
  onChange: (item: T | null) => void;

  getId: (item: T) => string;
  getLabel: (item: T) => string;

  filterFn?: (item: T, query: string) => boolean;
  onQueryChange?: (query: string) => void;
  onOpenChange?: (open: boolean) => void;
  /** A fetch for the dropdown's options is in flight. The caret swaps to a
   *  spinner for as long as this is true — the dropdown's one in-flight
   *  indicator. It lives in the trigger, not the list, so a re-search over
   *  the previous page (server mode keeps it on screen while the new `q` is
   *  in flight) is visible without shifting or dimming the rows: they stay
   *  readable and clickable, since they're the best answer available until
   *  the new page lands. The caret keeps its open/close behaviour.
   *
   *  When nothing has been fetched yet, the list area also shows
   *  `loadingText`, pulsing (`motion-safe`, so reduced-motion users get it
   *  static), so an open dropdown isn't a blank box.
   *
   *  Server-mode callers should wire this to the query's `isFetching`, not
   *  `isLoading`: the latter goes false as soon as ANY page has been cached
   *  for the key, which is exactly the re-search case above. */
  loading?: boolean;

  renderItem?: (item: T, ctx: ComboboxItemContext) => React.ReactNode;
  renderTrailing?: (item: T) => React.ReactNode;
  /** Visual badge rendered inside the input, anchored to the left edge,
   *  shown only when an item is selected and the popover is closed. Mirror
   *  of `renderTrailing` for the leading side. Use this to surface a
   *  small disambiguator (e.g. a service-role chip) that text-only labels
   *  can't carry. The input gets extra left padding so the badge doesn't
   *  cover the displayed label. */
  renderLeading?: (item: T) => React.ReactNode;
  renderEmpty?: (query: string) => React.ReactNode;
  renderFooter?: (query: string, close: () => void) => React.ReactNode;
  /** Optional grouping. When supplied, the dropdown emits a non-interactive
   *  header before the first visible item of each contiguous run of items
   *  sharing a key — i.e. the caller controls the row order and gets
   *  sectioning for free. Keyboard navigation still hops option-to-option;
   *  headers are visual only.
   *
   *  Both `getKey` and `renderHeader` are required when grouping is
   *  enabled — they're paired in a single prop so a caller can't supply
   *  one half and silently lose sectioning. */
  grouping?: ComboboxGrouping<T>;

  onCreateNew?: (query: string) => void;
  createNewLabel?: string;

  id?: string;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  emptyText?: string;
  /** Shown in the list area while nothing has been fetched yet. Override
   *  where "Loading…" is wrong for the domain (e.g. "Searching customers…"). */
  loadingText?: string;
  /** Open the popover on mount. Used by inline-edit cells that activate the
   *  picker on demand and want the dropdown to appear immediately. */
  defaultOpen?: boolean;
  /** Whether emptying the input and blurring should clear the selection.
   *  Default true. Set false where "no value" is not a valid state (e.g.
   *  an existing timesheet row whose customer can only be swapped). */
  clearable?: boolean;
  /** Popover sizing.
   *  - `'anchor'` (default): match the trigger width — tidy when items are
   *    short or the trigger is already wide.
   *  - `'auto'`: use the trigger width as a min, but let the popover grow
   *    to fit the widest row up to `max-w-3xl` (48rem). Pick this when item
   *    labels are routinely longer than the trigger (e.g. a product picker
   *    mounted in a narrow grid cell). */
  popoverWidth?: 'anchor' | 'auto';
}

export function Combobox<T>({
  items,
  selectedItem,
  value,
  onChange,
  getId,
  getLabel,
  filterFn,
  onQueryChange,
  onOpenChange,
  loading,
  renderItem,
  renderTrailing,
  renderLeading,
  renderEmpty,
  renderFooter,
  grouping,
  onCreateNew,
  createNewLabel = 'Create new',
  id,
  placeholder = 'Select…',
  disabled,
  hasError,
  className,
  emptyText = 'No matches',
  loadingText = 'Loading…',
  defaultOpen = false,
  clearable = true,
  popoverWidth = 'anchor',
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [inputValue, setInputValue] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  // True iff the user has typed since the last selection or external sync.
  // Distinguishes "user is searching" from "input still shows the selected
  // label" without the brittle `inputValue === displayLabel` heuristic.
  const [hasUserTyped, setHasUserTyped] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isSelectingRef = React.useRef(false);

  const listboxId = React.useId();
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  // Mounted already-open (`defaultOpen`) — put the caret in the input so the
  // trigger acts as the search box straight away. Callers that mount this way
  // swap a label/button out for the picker (the timesheet week grid's customer
  // and activity cells), so the element that had focus is gone by the time we
  // render and focus would otherwise land on <body>: the list appears, but
  // typing and arrow keys do nothing until the user notices the field and
  // clicks it. Radix's own focus-on-open is deliberately suppressed
  // (`onOpenAutoFocus` below) because it would focus the popover content
  // rather than the input, so this is the only thing that can do it.
  // Mount-only by design: reopening later goes through `handleToggle` /
  // `handleFocus`, which already leave focus on the input.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only is the point — re-running when `defaultOpen` flips would grab focus back from wherever the user had since put it.
  React.useEffect(() => {
    if (defaultOpen) inputRef.current?.focus();
  }, []);

  // Resolve the selected item: prefer the explicit prop (lets parents pass
  // an async-loaded record), fall back to scanning `items`. No memo — the
  // computation is trivial and the deps (inline arrow getId from callers)
  // invalidate every render anyway.
  const resolvedSelected: T | null =
    selectedItem ?? (value == null ? null : (items.find((it) => getId(it) === value) ?? null));
  const displayLabel = resolvedSelected ? getLabel(resolvedSelected) : '';

  // Sync the input back to the selected label whenever the popover closes
  // or the selection changes from the outside.
  React.useEffect(() => {
    if (!open) {
      setInputValue(displayLabel);
      setHasUserTyped(false);
    }
  }, [displayLabel, open]);

  const effectiveQuery = hasUserTyped ? inputValue.trim() : '';

  // Surface query changes for server-side search. Only fires when the user
  // is actually typing, not on the initial mount or on selection.
  React.useEffect(() => {
    if (!hasUserTyped) return;
    onQueryChange?.(effectiveQuery);
  }, [effectiveQuery, hasUserTyped, onQueryChange]);

  // Surface open/close so parents can gate `useQuery({ enabled: open })`.
  React.useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const visibleItems =
    !filterFn || !effectiveQuery ? items : items.filter((it) => filterFn(it, effectiveQuery));

  const scrollItemIntoView = (index: number) => {
    const el = document.getElementById(optionId(index));
    el?.scrollIntoView({ block: 'nearest' });
  };

  const selectItem = (item: T) => {
    isSelectingRef.current = true;
    onChange(item);
    setInputValue(getLabel(item));
    setHasUserTyped(false);
    setOpen(false);
    setHighlightedIndex(-1);
    requestAnimationFrame(() => {
      isSelectingRef.current = false;
    });
  };

  const handleCreateNew = () => {
    if (!onCreateNew) return;
    isSelectingRef.current = true;
    setOpen(false);
    setHighlightedIndex(-1);
    onCreateNew(effectiveQuery);
    requestAnimationFrame(() => {
      isSelectingRef.current = false;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setHasUserTyped(true);
    setHighlightedIndex(-1);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    if (!open) setOpen(true);
  };

  // Reopen on click even when the input already holds focus. After a
  // selection, `onMouseDown→preventDefault` on the option keeps focus on
  // the input, so a subsequent click fires no fresh `focus` event — without
  // this handler the popover could never be reopened by clicking.
  const handleClick = () => {
    if (!disabled && !open) setOpen(true);
  };

  // Explicit clear affordance (the inline `X`). Mirrors the empty-input-on-
  // blur path but is reachable in one click while a value is shown.
  // `onMouseDown→preventDefault` (on the button) keeps focus on the input so
  // the trailing blur doesn't fight this; we close rather than reopen.
  const handleClear = () => {
    onChange(null);
    setInputValue('');
    setHasUserTyped(false);
    setHighlightedIndex(-1);
    setOpen(false);
  };

  // The chevron is a real toggle button (not a decorative icon): clicking it
  // opens the dropdown when closed and closes it when open, the way a <select>
  // caret behaves. `onMouseDown→preventDefault` on the button keeps focus on
  // the input, so on open we focus it explicitly for typing/keyboard nav.
  const handleToggle = () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
      setHighlightedIndex(-1);
    } else {
      setOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const related = e.relatedTarget as Node | null;
    if (related && containerRef.current?.contains(related)) return;
    requestAnimationFrame(() => {
      if (isSelectingRef.current) return;
      setOpen(false);
      setHighlightedIndex(-1);
      // Empty input on blur clears the selection — replaces the old `X`
      // button affordance from the prior autocompletes. Suppressed when
      // `clearable` is false (e.g. timesheet rows that can only be swapped).
      if (clearable && value && inputValue.trim() === '') {
        onChange(null);
        setInputValue('');
      } else {
        setInputValue(displayLabel);
      }
      setHasUserTyped(false);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        const next = highlightedIndex < visibleItems.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(next);
        scrollItemIntoView(next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        const prev = highlightedIndex > 0 ? highlightedIndex - 1 : visibleItems.length - 1;
        setHighlightedIndex(prev);
        scrollItemIntoView(prev);
        break;
      }
      case 'Enter': {
        e.preventDefault();
        // If nothing's highlighted, treat Enter as "accept the top match" —
        // standard autocomplete UX. If there are no matches, no-op.
        const picked = highlightedIndex >= 0 ? visibleItems[highlightedIndex] : visibleItems[0];
        if (picked) selectItem(picked);
        break;
      }
      case 'Tab': {
        // Accept-on-Tab, conservatively: commit only when the choice is
        // unambiguous — an explicitly highlighted row, or a single remaining
        // match. Ambiguous multi-match queries fall through to the native
        // blur, which reverts to the last committed label. We never
        // preventDefault, so focus still advances to the next field; the
        // `isSelectingRef` guard in `handleBlur` keeps the trailing blur from
        // undoing the selection we just made.
        if (!hasUserTyped) break;
        const picked =
          highlightedIndex >= 0
            ? visibleItems[highlightedIndex]
            : visibleItems.length === 1
              ? visibleItems[0]
              : undefined;
        if (picked) selectItem(picked);
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
        break;
      }
    }
  };

  const trailingContent = !open && resolvedSelected ? renderTrailing?.(resolvedSelected) : null;
  const leadingContent = !open && resolvedSelected ? renderLeading?.(resolvedSelected) : null;
  // Show the inline clear button when there's a value to clear and the
  // popover is closed. While open the chevron stays so the affordance
  // doesn't flicker mid-search.
  const showClear = clearable && !!resolvedSelected && !open && !disabled;

  // The leading badge (e.g. ServiceRoleChip) is free-width content — its
  // rendered width varies with the string it carries (a short "BK" code vs.
  // a longer custom role name). A fixed `pl-*` guess only fits the narrow
  const [leadingEl, setLeadingEl] = React.useState<HTMLSpanElement | null>(null);
  const [leadingWidth, setLeadingWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!leadingEl) {
      setLeadingWidth(0);
      return;
    }
    const measure = () => setLeadingWidth(leadingEl.offsetWidth);
    measure();
    // jsdom (unit tests) doesn't implement ResizeObserver — the initial
    // `measure()` call above still runs, so the fallback there is just no
    // re-measurement on later resize, not a broken test environment.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(leadingEl);
    return () => ro.disconnect();
  }, [leadingEl]);
  // left-2 offset the badge sits at, plus a small gap before the label text.
  const LEADING_OFFSET_PX = 8;
  const LEADING_GAP_PX = 6;
  const leadingPaddingPx = leadingContent ? LEADING_OFFSET_PX + leadingWidth + LEADING_GAP_PX : 0;

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <div ref={containerRef} className="relative w-full self-start">
          <Input
            ref={inputRef}
            id={id}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onClick={handleClick}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-controls={open ? listboxId : undefined}
            aria-activedescendant={
              open && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined
            }
            autoComplete="off"
            className={cn(
              'w-full',
              // Leave room for the trailing icons: caret alone (pr-8), clear +
              // caret (pr-14), or a renderTrailing badge plus both (pr-24).
              trailingContent ? 'pr-24' : showClear ? 'pr-14' : 'pr-8',
              // Fallback left padding for the leading badge before its real
              // width is measured (first paint only — see leadingPaddingPx).
              leadingContent ? 'pl-14' : undefined,
              hasError && 'border-destructive ring-1 ring-destructive/20',
              disabled && 'disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100',
              className,
            )}
            style={leadingWidth > 0 ? { paddingLeft: leadingPaddingPx } : undefined}
          />
          {leadingContent ? (
            <span
              ref={setLeadingEl}
              // `flex` so the badge is centred as a box, not placed on a text
              // line's baseline inside this span — which left it a few px high.
              className="pointer-events-none absolute left-2 top-1/2 flex -translate-y-1/2 items-center"
            >
              {leadingContent}
            </span>
          ) : null}
          {trailingContent ? (
            <span
              className={cn(
                'pointer-events-none absolute top-1/2 max-w-[40%] -translate-y-1/2 truncate text-xs text-muted-foreground',
                // Sit left of the clear button when it's showing, else just
                // left of the caret.
                showClear ? 'right-14' : 'right-8',
              )}
            >
              {trailingContent}
            </span>
          ) : null}
          {showClear ? (
            <button
              type="button"
              aria-label="Clear selection"
              tabIndex={-1}
              // Keep focus on the input so the resulting blur doesn't double-
              // fire the clear path; the click handler does the clearing.
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="absolute right-7 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground/60 transition-colors hover:text-foreground"
            >
              <X size={14} />
            </button>
          ) : null}
          {/* Always-present caret toggle. Replaces the old pointer-events-none
              icon so the dropdown can be opened/closed by clicking the caret —
              the conventional <select>-style affordance. While `loading`, the
              chevron becomes a spinner: same button, same toggle, only the
              glyph changes. */}
          <button
            type="button"
            aria-label={open ? 'Close dropdown' : 'Open dropdown'}
            tabIndex={-1}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleToggle}
            className={cn(
              'absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
              // The idle chevron is deliberately faint; a thin spinning arc in
              // the same /50 tint all but vanishes, so loading gets full weight.
              loading ? 'text-muted-foreground' : 'text-muted-foreground/50',
            )}
          >
            {loading ? (
              <Loader2 size={16} aria-hidden="true" className="animate-spin" />
            ) : (
              <ChevronDown size={16} className={cn('transition-transform', open && 'rotate-180')} />
            )}
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn(
          'overflow-hidden p-0',
          // shadcn's `PopoverContent` ships with `w-72` (18rem) as a base
          // class. Without `w-auto` here, twMerge keeps the fixed 18rem
          // width and our min/max constraints have nothing to interpolate
          // between — the popover never grows past 288px no matter how
          // long the rows are.
          popoverWidth === 'auto' && 'w-auto max-w-3xl',
        )}
        style={
          popoverWidth === 'auto'
            ? { minWidth: 'var(--radix-popper-anchor-width)' }
            : {
                width: 'var(--radix-popper-anchor-width)',
                minWidth: 'var(--radix-popper-anchor-width)',
              }
        }
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          const target = e.target as Node | null;
          if (target && containerRef.current?.contains(target)) {
            e.preventDefault();
            return;
          }
          setOpen(false);
          setHighlightedIndex(-1);
        }}
      >
        <div className="flex max-h-80 flex-col">
          {/* popoverScrollFix: this list is portaled, so a wrapping Sheet/Dialog's
              scroll-lock would otherwise eat wheel events and the list (capped at
              max-h-80) couldn't be scrolled. See lib/popover-scroll-fix.ts. */}
          <div className="flex-1 overflow-y-auto" {...popoverScrollFix}>
            {/* "Nothing has arrived yet" — `items`, not `visibleItems`. A
                client-mode filter that matches nothing leaves `visibleItems`
                empty over a fully loaded catalog, and keying this branch on
                that turned a legitimate "No matches" into "Loading…" for the
                whole of any background revalidation (preload's data goes
                stale at 5 min and `refetchOnMount` is left on), swallowing
                `renderEmpty`/`emptyText` with it. With `items` the two states
                are independent: the caret spinner reports the refetch while
                this branch still says the filter matched nothing. No spinner
                here — that lives on the caret; the text pulses instead. */}
            {loading && items.length === 0 ? (
              <div
                role="status"
                className="px-3 py-6 text-center text-sm text-muted-foreground motion-safe:animate-pulse"
              >
                {loadingText}
              </div>
            ) : visibleItems.length > 0 ? (
              <div id={listboxId} role="listbox" aria-busy={loading || undefined} className="py-1">
                {renderListboxBody({
                  visibleItems,
                  grouping,
                  getId,
                  getLabel,
                  value,
                  highlightedIndex,
                  effectiveQuery,
                  renderItem,
                  optionId,
                  selectItem,
                  setHighlightedIndex,
                })}
              </div>
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {renderEmpty ? renderEmpty(effectiveQuery) : emptyText}
              </div>
            )}
          </div>
          {renderFooter ? (
            <div className="border-t">{renderFooter(effectiveQuery, () => setOpen(false))}</div>
          ) : onCreateNew ? (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleCreateNew}
              className="flex w-full items-center gap-2 border-t px-3 py-2 text-sm text-primary hover:bg-accent hover:text-accent-foreground"
            >
              <Plus className="size-4" />
              <span>{createNewLabel}</span>
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ComboboxOptionProps {
  optionId: string;
  isSelected: boolean;
  isHighlighted: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  children: React.ReactNode;
}

function ComboboxOption({
  optionId,
  isSelected,
  isHighlighted,
  onClick,
  onMouseEnter,
  children,
}: ComboboxOptionProps) {
  return (
    <button
      type="button"
      id={optionId}
      role="option"
      aria-selected={isSelected}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        'block w-full cursor-pointer px-3 py-2 text-left text-sm outline-none transition-colors',
        // Highlighted (hovered/keyboard-navigated, not yet chosen) takes the
        // same pale lime a table row hovers to (`--row-hover`) — not the blue
        // Selection wash, which is reserved for `isSelected` below.
        isHighlighted &&
          !isSelected &&
          'bg-accent text-accent-foreground light:bg-row-hover light:text-foreground',
        isSelected && 'bg-accent text-accent-foreground',
      )}
    >
      {children}
    </button>
  );
}

function DefaultRow({ label, isSelected }: { label: string; isSelected: boolean }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <CheckSlot isSelected={isSelected} />
      <span className="truncate">{label}</span>
    </div>
  );
}

/**
 * Render the listbox body — each visible item as a `<ComboboxOption>`, with
 * optional group headers emitted before the first item of each contiguous
 * run sharing a group key. Pulled out of the render so the group-header
 * decision (which needs single-pass state across items) reads as a plain
 * `for…of` loop instead of a mutable closure inside `.map()`.
 */
function renderListboxBody<T>({
  visibleItems,
  grouping,
  getId,
  getLabel,
  value,
  highlightedIndex,
  effectiveQuery,
  renderItem,
  optionId,
  selectItem,
  setHighlightedIndex,
}: {
  visibleItems: readonly T[];
  grouping: ComboboxGrouping<T> | undefined;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  value: string | null;
  highlightedIndex: number;
  effectiveQuery: string;
  renderItem: ((item: T, ctx: ComboboxItemContext) => React.ReactNode) | undefined;
  optionId: (index: number) => string;
  selectItem: (item: T) => void;
  setHighlightedIndex: (i: number) => void;
}): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let prevGroupKey: string | null = null;
  for (let index = 0; index < visibleItems.length; index++) {
    const item = visibleItems[index] as T;
    const itemId = getId(item);
    const isSelected = itemId === value;
    const isHighlighted = index === highlightedIndex;
    const groupKey = grouping ? grouping.getKey(item) : null;
    const headerNode =
      grouping && groupKey !== prevGroupKey ? grouping.renderHeader(groupKey as string) : null;
    prevGroupKey = groupKey;
    nodes.push(
      <React.Fragment key={itemId}>
        {headerNode}
        <ComboboxOption
          optionId={optionId(index)}
          isSelected={isSelected}
          isHighlighted={isHighlighted}
          onClick={() => selectItem(item)}
          onMouseEnter={() => {
            // Guard the setState so mouse jitter within a row doesn't
            // trigger a Combobox-wide re-render on every pointer event.
            if (highlightedIndex !== index) setHighlightedIndex(index);
          }}
        >
          {renderItem ? (
            renderItem(item, { isSelected, isHighlighted, query: effectiveQuery })
          ) : (
            <DefaultRow label={getLabel(item)} isSelected={isSelected} />
          )}
        </ComboboxOption>
      </React.Fragment>,
    );
  }
  return nodes;
}
